import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DatePicker, { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import './AppointmentBooking.css';

registerLocale('de', de);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RESCHEDULE_LIMIT = 2;

// Roles that can see lead zoom meetings in the calendar
const LEAD_CALENDAR_ROLES = ['admin', 'backoffice', 'vertriebsleiter'];

const AppointmentList = ({ currentUser, refreshKey = 0 }) => {
  const [appointments, setAppointments] = useState([]);
  const [leadMeetings, setLeadMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rescheduleCount, setRescheduleCount] = useState(0);
  const [showRescheduleModal, setShowRescheduleModal] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [resetLoadingId, setResetLoadingId] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());

  const canManage = ['admin', 'teamleiter', 'vertriebsleiter'].includes(currentUser?.role);
  const isVertriebler = currentUser?.role === 'vertriebler';
  const isVertriebsleiter = currentUser?.role === 'vertriebsleiter';
  const showLeadMeetings = LEAD_CALENDAR_ROLES.includes(currentUser?.role);

  useEffect(() => {
    fetchAppointments();
    if (isVertriebler) fetchRescheduleCount();
    if (showLeadMeetings) fetchLeadZoomMeetings();
  }, [refreshKey]); // refreshKey triggers re-fetch; fetch functions are stable refs

  const fetchLeadZoomMeetings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/leads/my-zoom-meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadMeetings(res.data || []);
    } catch {
      // silently ignore – lead meetings are supplementary
    }
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/appointments/appointments/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data);
    } catch {
      setError('Fehler beim Laden der Termine');
    } finally {
      setLoading(false);
    }
  };

  const fetchRescheduleCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRescheduleCount(res.data.appointment_reschedule_count || 0);
    } catch {}
  };


  const handleReschedule = async () => {
    if (!newDate || !showRescheduleModal) return;
    setRescheduling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/appointments/${showRescheduleModal}/reschedule`,
        { new_date: new Date(newDate).toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRescheduleCount(res.data.reschedule_count);
      setShowRescheduleModal(null);
      setNewDate('');
      fetchAppointments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Verschieben');
    } finally {
      setRescheduling(false);
    }
  };

  const handleResetReschedule = async (userId) => {
    setResetLoadingId(userId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/appointments/users/${userId}/reset-reschedule`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAppointments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setResetLoadingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const cfg = {
      confirmed: { color: 'bg-green-100 text-green-800', label: 'Bestätigt' },
      pending:   { color: 'bg-yellow-100 text-yellow-800', label: 'Ausstehend' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Abgesagt' },
      completed: { color: 'bg-blue-100 text-blue-800', label: 'Abgeschlossen' }
    };
    const c = cfg[status] || cfg.pending;
    return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }),
      dateLong: date.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
      time: date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const isUpcoming = (d) => new Date(d) > new Date();
  const sorted = [...appointments].sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date));
  const upcoming = sorted.filter(a => isUpcoming(a.appointment_date));

  const minDateTime = new Date(Date.now() + 3600000).toISOString().slice(0, 16);

  // --- Calendar helpers for VL ---
  const appointmentsByDate = useMemo(() => {
    const map = {};
    for (const appt of appointments) {
      const d = new Date(appt.appointment_date);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!map[key]) map[key] = [];
      map[key].push({ ...appt, _type: 'appointment' });
    }
    // Merge lead zoom meetings
    for (const lead of leadMeetings) {
      const zm = lead.zoom_meeting;
      if (!zm?.date) continue;
      const d = new Date(zm.date + 'T12:00:00');
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!map[key]) map[key] = [];
      map[key].push({
        ...lead,
        _type: 'lead',
        appointment_date: `${zm.date}T${zm.time || '00:00'}:00`,
        duration_minutes: 60,
        status: zm.status === 'completed' ? 'completed' : zm.status === 'cancelled' ? 'cancelled' : 'confirmed',
        zoom_meeting_url: zm.link,
        vertriebler_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
      });
    }
    // Sort each day's appointments by time
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
    }
    return map;
  }, [appointments, leadMeetings]);

  const calendarKey = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth()+1).padStart(2,'0')}-${String(calendarDate.getDate()).padStart(2,'0')}`;
  const selectedDayAppointments = appointmentsByDate[calendarKey] || [];

  const getDayClassName = (date) => {
    const key = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    const dayItems = appointmentsByDate[key];
    if (!dayItems || dayItems.length === 0) return '';
    const hasLead = dayItems.some(a => a._type === 'lead' && a.status !== 'cancelled');
    const hasAppt = dayItems.some(a => a._type === 'appointment' && a.status !== 'cancelled');
    const allCancelled = dayItems.every(a => a.status === 'cancelled');
    if (allCancelled) return 'all-cancelled-date';
    if (hasLead && hasAppt) return 'mixed-date';
    if (hasLead) return 'lead-date';
    return 'available-date';
  };

  // Stats
  const stats = {
    total: appointments.length,
    upcoming: upcoming.length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    cancelled: appointments.filter(a => a.status === 'cancelled').length,
  };

  if (loading) return (
    <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
  );

  if (appointments.length === 0) return (
    <div className="bg-white rounded-xl shadow-lg p-8 text-center">
      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth={1.5} /><line x1="16" y1="2" x2="16" y2="6" strokeWidth={1.5} /><line x1="8" y1="2" x2="8" y2="6" strokeWidth={1.5} /><line x1="3" y1="10" x2="21" y2="10" strokeWidth={1.5} />
      </svg>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Termine</h3>
      <p className="text-gray-600">Es sind noch keine Termine vorhanden.</p>
    </div>
  );

  // ─── VL CALENDAR VIEW ───
  if (isVertriebsleiter) {
    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            <p className="text-xs text-gray-500 mt-1">Gesamt</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.upcoming}</p>
            <p className="text-xs text-gray-500 mt-1">Anstehend</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
            <p className="text-xs text-gray-500 mt-1">Bestätigt</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-red-500">{stats.cancelled}</p>
            <p className="text-xs text-gray-500 mt-1">Abgesagt</p>
          </div>
        </div>

        {/* Calendar + Day Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="vl-calendar">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Terminkalender
            </h2>
            <DatePicker
              selected={calendarDate}
              onChange={(date) => setCalendarDate(date)}
              locale="de"
              inline
              dayClassName={getDayClassName}
              calendarClassName="modern-calendar"
              showPopperArrow={false}
            />
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
                Abschlussgespräch
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-purple-500 inline-block"></span>
                Lead-Gespräch
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-400 inline-block"></span>
                Alle abgesagt
              </span>
            </div>
          </div>

          {/* Day Detail */}
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="vl-day-detail">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {calendarDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h2>
            {selectedDayAppointments.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <p className="text-sm font-medium">Keine Termine an diesem Tag</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDayAppointments.map(appt => {
                  const { time } = formatDateTime(appt.appointment_date);
                  const isPast = new Date(appt.appointment_date) < new Date();
                  const isLead = appt._type === 'lead';
                  return (
                    <div key={appt.id}
                      className={`border rounded-xl p-4 transition-shadow hover:shadow-md ${
                        isPast
                          ? 'border-gray-200 bg-gray-50 opacity-70'
                          : isLead
                            ? 'border-purple-200 bg-purple-50/40'
                            : 'border-blue-200 bg-blue-50/30'
                      }`}
                      data-testid={`cal-appt-${appt.id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-sm font-bold text-gray-900">{time} Uhr</span>
                            {isLead ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Lead-Gespräch</span>
                            ) : (
                              getStatusBadge(appt.status)
                            )}
                            {isPast && <span className="text-xs text-gray-400 italic">vergangen</span>}
                          </div>
                          {isLead ? (
                            <>
                              <p className="text-sm text-gray-700">
                                Erstgespräch: <span className="font-medium">{appt.vertriebler_name}</span>
                              </p>
                              {appt.zoom_meeting?.person && (
                                <p className="text-xs text-gray-400 mt-0.5">Zuständig: {appt.zoom_meeting.person}</p>
                              )}
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-gray-600">
                                Abschlussgespräch mit <span className="font-medium">{appt.vertriebler_name}</span>
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">{appt.duration_minutes} Min.</p>
                            </>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {appt.zoom_meeting_url && !isPast && (
                            <a href={appt.zoom_meeting_url} target="_blank" rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-xs font-medium transition-colors ${isLead ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                              data-testid={`zoom-link-${appt.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                              </svg>
                              Zoom
                            </a>
                          )}
                          {canManage && !isLead && (
                            <button
                              onClick={() => handleResetReschedule(appt.vertriebler_id)}
                              disabled={resetLoadingId === appt.vertriebler_id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                              data-testid={`reset-reschedule-${appt.vertriebler_id}`}>
                              {resetLoadingId === appt.vertriebler_id ? (
                                <span className="animate-spin w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full"/>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                </svg>
                              )}
                              Limit reset
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── DEFAULT LIST VIEW (Vertriebler / Teamleiter) ───
  return (
    <div className="space-y-6">
      {/* Reschedule limit info for Vertriebler */}
      {isVertriebler && rescheduleCount > 0 && (
        <div className={`rounded-xl p-4 flex items-center gap-3 ${
          rescheduleCount >= RESCHEDULE_LIMIT
            ? 'bg-red-50 border border-red-200 text-red-700'
            : 'bg-amber-50 border border-amber-200 text-amber-700'
        }`} data-testid="reschedule-info">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <span className="text-sm font-medium">
            {rescheduleCount >= RESCHEDULE_LIMIT
              ? `Verschiebungslimit erreicht (${RESCHEDULE_LIMIT}/${RESCHEDULE_LIMIT}) – bitte wende dich an deinen Teamleiter`
              : `Termin ${rescheduleCount}x verschoben – noch ${RESCHEDULE_LIMIT - rescheduleCount}x möglich`}
          </span>
        </div>
      )}

      {/* Upcoming Appointments */}
      {upcoming.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Anstehende Termine ({upcoming.length})
          </h2>
          <div className="space-y-4">
            {upcoming.map(appt => {
              const { dateLong, time } = formatDateTime(appt.appointment_date);
              const other = currentUser.role === 'vertriebsleiter' ? appt.vertriebler_name : appt.vertriebsleiter_name;
              const canReschedule = isVertriebler && appt.status !== 'cancelled' && rescheduleCount < RESCHEDULE_LIMIT;
              const limitReached = isVertriebler && rescheduleCount >= RESCHEDULE_LIMIT;

              return (
                <div key={appt.id} className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                  data-testid={`upcoming-appt-${appt.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900">
                          Abschlussgespräch mit {other}
                        </h3>
                        {getStatusBadge(appt.status)}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          {dateLong}
                        </p>
                        <p className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                          {time} Uhr ({appt.duration_minutes} Min.)
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {isVertriebler && appt.status !== 'cancelled' && (
                        <button
                          data-testid={`reschedule-btn-${appt.id}`}
                          onClick={() => canReschedule ? setShowRescheduleModal(appt.id) : null}
                          disabled={limitReached}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            limitReached
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100'
                          }`}
                          title={limitReached ? 'Verschiebungslimit erreicht' : `Termin verschieben`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                          Verschieben {rescheduleCount > 0 && `(${rescheduleCount}/${RESCHEDULE_LIMIT})`}
                        </button>
                      )}
                      {canManage && (
                        <button
                          data-testid={`reset-reschedule-${appt.vertriebler_id}`}
                          onClick={() => handleResetReschedule(appt.vertriebler_id)}
                          disabled={resetLoadingId === appt.vertriebler_id}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                          {resetLoadingId === appt.vertriebler_id ? (
                            <span className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full"/>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                            </svg>
                          )}
                          Limit zurücksetzen
                        </button>
                      )}
                    </div>
                  </div>

                  {appt.zoom_meeting_url && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <a href={appt.zoom_meeting_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                        </svg>
                        Zoom Meeting öffnen
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4">
              <h3 className="text-white font-bold text-lg">Termin verschieben</h3>
              <p className="text-amber-100 text-sm mt-0.5">
                {rescheduleCount + 1}. von max. {RESCHEDULE_LIMIT} Verschiebungen
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Neues Datum & Uhrzeit</label>
              <input
                type="datetime-local"
                value={newDate}
                min={minDateTime}
                onChange={e => setNewDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
                data-testid="reschedule-date-input"
              />
              <p className="text-xs text-gray-400 mt-2">
                Nach {RESCHEDULE_LIMIT} Verschiebungen ist kein weiteres Verschieben möglich.
              </p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => { setShowRescheduleModal(null); setNewDate(''); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleReschedule}
                disabled={!newDate || rescheduling}
                data-testid="confirm-reschedule-btn"
                className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                {rescheduling && <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"/>}
                Termin verschieben
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentList;
